/**
 * Archive tiers (0.29.0): restoring an original from an archive (S3 Glacier Flexible Retrieval or Deep
 * Archive, OCI Archive). The store takes minutes to hours to make it readable; PRISM then copies it to the
 * originals tier and the asset is online again. The archive copy is kept.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Loader2 } from 'lucide-react';
import type { AssetAvailability, RestoreDTO } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useMe } from '../api/hooks';
import { Modal } from './Modal';
import { toast } from './Toasts';
import { relative } from '../lib/format';
import { queryKeys } from '../api/queryKeys';

export const RESTORE_STATUS_LABEL: Record<RestoreDTO['status'], string> = {
  queued: 'Asking the archive', restoring: 'Waiting for the archive', copying: 'Copying to the originals tier', succeeded: 'Restored', failed: 'Failed',
};

/** Whoever may download originals or manage storage may ask for a restore. */
export function useCanRestore(): boolean {
  const me = useMe().data;
  return Boolean(me && (me.permissions.includes('asset.download') || me.permissions.includes('storage.manage')));
}

export function useRestores(assetId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['restores', assetId],
    queryFn: () => api<{ items: RestoreDTO[] }>(`/api/v1/restores?assetId=${assetId}&limit=10`),
    enabled: Boolean(assetId) && enabled,
    refetchInterval: (q) => (q.state.data?.items.some((r) => !['succeeded', 'failed'].includes(r.status)) ? 10_000 : false),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.assets() });
    void qc.invalidateQueries({ queryKey: ['asset'] });
    void qc.invalidateQueries({ queryKey: ['restores'] });
    void qc.invalidateQueries({ queryKey: ['preflight'] });
  };
}

function useRestoreAsset() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ assetId, tier }: { assetId: string; tier?: string | null }) => api<RestoreDTO>(`/api/v1/assets/${assetId}/restores`, { method: 'POST', json: tier ? { tier } : {} }),
    onSuccess: invalidate,
  });
}

export function useBulkRestore() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (assetIds: string[]) => api<{ queued: number; alreadyRestoring: number; skipped: Array<{ assetId: string; reason: string }> }>('/api/v1/restores', { method: 'POST', json: { assetIds } }),
    onSuccess: invalidate,
  });
}

const TIER_HELP: Record<string, string> = {
  '': 'The archive location’s setting',
  Expedited: 'Expedited: 1–5 minutes (Glacier Flexible Retrieval only; the most expensive)',
  Standard: 'Standard: 3–5 hours (Deep Archive: up to 12 hours)',
  Bulk: 'Bulk: 5–12 hours (Deep Archive: up to 48 hours; the cheapest)',
};

/** "Restore from archive" for one asset whose original is archived; a status line while it is restoring. */
export function RestoreFromArchive({ assetId, title, availability, size = 'sm' }: { assetId: string; title: string; availability: AssetAvailability; size?: 'sm' | 'md' }) {
  const canRestore = useCanRestore();
  const restores = useRestores(assetId, availability === 'archived' || availability === 'restoring');
  const restore = useRestoreAsset();
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState('');
  if (availability !== 'archived' && availability !== 'restoring') return null;
  const active = restores.data?.items.find((r) => !['succeeded', 'failed'].includes(r.status));
  const failed = !active ? restores.data?.items.find((r) => r.status === 'failed') : undefined;
  if (availability === 'restoring' || active) {
    return (
      <div className="restore-status" data-testid="restore-status" role="status">
        <Loader2 size={13} className="spin" />
        <span>
          <strong>Restoring from {active?.source.name ?? 'the archive'}</strong>
          {active && <span className="faint"> · {RESTORE_STATUS_LABEL[active.status]}{active.status === 'restoring' ? ` (usually ${active.expectedDuration})` : ''} · asked {relative(active.createdAt)}</span>}
        </span>
      </div>
    );
  }
  return (
    <>
      <div className="restore-status archived" data-testid="restore-archived">
        <Archive size={13} />
        <span>
          <strong>The original is archived</strong>
          <span className="faint"> · restore it to play the original, download it, export from it or process it again</span>
          {failed && <span className="warn-text" data-testid="restore-failed"> · The last restore failed: {failed.error?.message ?? 'unknown error'}</span>}
        </span>
        {canRestore && <button className={`btn ${size === 'sm' ? 'sm' : ''}`} onClick={() => setOpen(true)} data-testid="restore-button"><Archive size={13} /> Restore from archive</button>}
      </div>
      {open && (
        <Modal title={`Restore “${title}”`} onClose={() => setOpen(false)} actions={<>
          <button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button>
          <button className="btn primary" disabled={restore.isPending} data-testid="restore-confirm" onClick={() => restore.mutate({ assetId, tier: tier || null }, {
            onSuccess: (r) => { setOpen(false); toast(`Restore requested from ${r.source.name}; usually ${r.expectedDuration}`); },
            onError: (e) => toast(errorMessage(e), 'error'),
          })}>{restore.isPending ? <Loader2 size={14} className="spin" /> : <Archive size={14} />} Restore</button>
        </>}>
          <p className="muted" style={{ marginTop: 0 }}>The archive makes the original readable (the store may charge for the retrieval), then PRISM copies it to the originals tier. The copy in the archive stays.</p>
          <div className="field">
            <label htmlFor="restore-tier">Retrieval speed</label>
            <select id="restore-tier" className="select" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="restore-tier">
              {Object.entries(TIER_HELP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <span className="help">OCI Archive has one speed (up to several hours) and ignores this.</span>
          </div>
        </Modal>
      )}
    </>
  );
}
